<template>
  <div class="project-settings-template">
    <AppSectionHero
      title="General"
      :subtitle="`Manage the working general settings template for ${applicationLabel}.`"
      :show-refresh="false"
    />

    <CCard class="border-0 shadow-sm template-shell">
      <CCardBody class="p-4">
        <div class="template-shell__eyebrow">Project Template</div>
        <div class="template-shell__title">General Settings</div>
        <div class="template-shell__copy">This template now includes profile context and a usable preferences form directly in the project sidebar flow.</div>

        <div class="template-grid">
          <div class="template-card">
            <div class="template-card__title">Profile Snapshot</div>
            <div class="template-card__copy">Use this block as the baseline profile section for project-specific settings pages.</div>

            <div class="info-list">
              <div class="info-row"><span>Name</span><strong>{{ displayName }}</strong></div>
              <div class="info-row"><span>Email</span><strong>{{ email }}</strong></div>
              <div class="info-row"><span>Role</span><strong>{{ roleLabel }}</strong></div>
              <div class="info-row"><span>Application</span><strong>{{ applicationLabel }}</strong></div>
            </div>

            <button class="primary-btn template-card__button" @click="$router.push('/general/profile')">
              <CIcon name="cil-user" />
              Open Full Profile
            </button>
          </div>

          <div class="template-card">
            <div class="template-card__title">Preferences</div>
            <div class="template-card__copy">Save local preferences here without leaving the project template workspace.</div>

            <CForm @submit.prevent="savePreferences">
              <label class="template-label">Language</label>
              <select v-model="form.language" class="form-control template-control">
                <option value="en">English</option>
                <option value="th">Thai</option>
              </select>

              <label class="template-check">
                <input v-model="form.emailNotifications" type="checkbox">
                <span>Email notifications</span>
              </label>
              <label class="template-check">
                <input v-model="form.desktopNotifications" type="checkbox">
                <span>Desktop notifications</span>
              </label>
              <label class="template-check">
                <input v-model="form.rememberSidebar" type="checkbox">
                <span>Remember sidebar state</span>
              </label>

              <div class="template-actions">
                <button class="primary-btn template-card__button" type="submit">
                  <CIcon name="cil-save" />
                  Save Preferences
                </button>
                <button class="secondary-btn template-card__button" type="button" @click="$router.push('/general/preferences')">
                  <CIcon name="cil-settings" />
                  Open Full Preferences
                </button>
              </div>
            </CForm>

            <div class="template-status" v-if="savedMessage">{{ savedMessage }}</div>
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import { getRoleLabel, getWorkspaceRole } from '@/utils/role-dashboard'
import {
  applyWorkspacePreferences,
  readWorkspacePreferences,
  saveWorkspacePreferences
} from '@/utils/workspace-preferences'

function pickText(value) {
  if (!value) return ''
  if (typeof value === 'string') return value.trim()
  if (Array.isArray(value)) {
    const found = value.find(item => item && item.value)
    return found && found.value ? String(found.value).trim() : ''
  }
  if (typeof value === 'object') {
    return String(value.value || value.name || value.label || '').trim()
  }
  return ''
}

export default {
  name: 'ProjectSettingsGeneral',
  components: { AppSectionHero },
  data() {
    return {
      form: {
        language: 'en',
        emailNotifications: true,
        desktopNotifications: false,
        rememberSidebar: true
      },
      savedMessage: ''
    }
  },
  computed: {
    applicationLabel() {
      return this.$route.params.application || 'this project'
    },
    profile() {
      return this.$store.getters['auth/profile'] || {}
    },
    displayName() {
      const userinfo = this.profile.userinfo || {}
      const prefix = pickText(userinfo.prefix)
      const firstName = pickText(userinfo.firstName) || pickText(this.profile.firstName)
      const lastName = pickText(userinfo.lastName) || pickText(this.profile.lastName)
      return [prefix, firstName, lastName].filter(Boolean).join(' ').trim() || this.profile.email || 'User'
    },
    email() {
      return this.profile.email || '-'
    },
    roleLabel() {
      return getRoleLabel(getWorkspaceRole(this.$store))
    }
  },
  mounted() {
    this.loadPreferences()
  },
  methods: {
    loadPreferences() {
      this.form = Object.assign({}, this.form, readWorkspacePreferences())
    },
    savePreferences() {
      saveWorkspacePreferences(this.form)
      applyWorkspacePreferences({ store: this.$store, i18nInstance: this.$i18n })
      this.savedMessage = 'Preferences saved for this browser.'
      window.setTimeout(() => {
        this.savedMessage = ''
      }, 2500)
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("project-settings-template");
@include workspace-button-system;

.template-shell {
  border-radius: 22px;
}

.template-shell__eyebrow {
  color: #8c1515;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.template-shell__title {
  color: #223045;
  font-size: 1.22rem;
  font-weight: 800;
}

.template-shell__copy {
  color: #66758a;
  line-height: 1.6;
  margin-top: 6px;
}

.template-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  margin-top: 20px;
}

.template-card {
  background: linear-gradient(180deg, #fffefc 0%, #ffffff 100%);
  border: 1px solid #e9edf3;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
}

.template-card__title {
  color: #223045;
  font-size: 1rem;
  font-weight: 800;
}

.template-card__copy {
  color: #66758a;
  line-height: 1.6;
}

.info-list {
  display: grid;
  gap: 10px;
  margin-top: 6px;
}

.info-row {
  align-items: center;
  border-top: 1px solid #eef2f7;
  display: flex;
  justify-content: space-between;
  padding-top: 10px;
}

.info-row span {
  color: #66758a;
}

.info-row strong {
  color: #223045;
  text-align: right;
}

.template-label {
  color: #223045;
  display: block;
  font-size: 0.88rem;
  font-weight: 700;
  margin: 10px 0 8px;
}

.template-control {
  border-radius: 12px;
  min-height: 44px;
}

.template-check {
  align-items: center;
  color: #4a596d;
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.template-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}

.template-card__button {
  align-self: flex-start;
}

.template-status {
  color: #15803d;
  font-weight: 700;
  margin-top: 6px;
}

@media (max-width: 768px) {
  .template-card__button {
    width: 100%;
  }
}
</style>
